import React, { useState, useEffect, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { translate } from 'react-i18next'
import { connect } from 'react-redux'
import SelectField from 'component/Select/Select'
import Api from 'utils/ApiRequest'
import API from 'constants/api'

const ThemeSelectField = ({
  label,
  placeholder,
  value,
  onChange,
  required,
  errorText,
  showHelperArea,
  validateValue,
  user,
  disabled,
  className,
  fill,
  showAllOption,
  translate,
  disableLoading
}) => {
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const retryRef = useRef(0)
  const timeoutRef = useRef(null)

  useEffect(() => {
    if (!user?.dsToken) return

    retryRef.current = 0

    const fetchData = async () => {
      if (retryRef.current >= 3) {
        setLoading(false)
        return
      }

      const hasValue =
        typeof value === 'string' ||
        (typeof value === 'object' && value?.value)

      const shouldShowLoading = !(disableLoading && hasValue)
      if (shouldShowLoading) setLoading(true)

      try {
        const api = new Api(user.dsToken)

        timeoutRef.current = setTimeout(() => {
          if (retryRef.current < 3) {
            retryRef.current++
            fetchData()
          }
        }, 5000)

        const response = await api.get(API.FETCH_PRICEBANNER_THEMES)
        clearTimeout(timeoutRef.current)

        const data = Array.isArray(response?.data) ? response.data : []

        const mapped = data.map(item => ({
          value: item.code,
          label: item.name
        }))

        const sorted = mapped.sort((a, b) =>
          a.label.localeCompare(b.label)
        )

        const finalOptions = showAllOption
          ? [{ label: translate('option.all'), value: 'todos' }, ...sorted]
          : sorted

        setOptions(finalOptions)
        retryRef.current = 0
      } catch (error) {
        clearTimeout(timeoutRef.current)
        if (retryRef.current < 3) {
          retryRef.current++
          fetchData()
        } else {
          setOptions([])
        }
      } finally {
        setLoading(false)
      }
    }

    fetchData()
    return () => clearTimeout(timeoutRef.current)
  }, [user?.dsToken, showAllOption, translate, disableLoading])

  const defaultLabel =
    label || translate('newPriceBannerFragment.theme')

  const defaultPlaceholder =
    placeholder ||
    translate('config.pricebannerConfig.form.cdThemeLabel2')

  const placeholderLabel =
    disableLoading || (!loading && options.length > 0)
      ? defaultPlaceholder
      : translate('loading')

 
  const resolvedValue = useMemo(() => {
    if (!value) return null

    if (typeof value === 'string') {
      return options.find(opt => opt.value === value) || null
    }

    if (typeof value === 'object' && value.value) {
      return value
    }

    return null
  }, [value, options])

  return (
    <SelectField
      label={defaultLabel}
      placeholder={placeholderLabel}
      value={resolvedValue}
      options={options}
      onChange={onChange}
      required={required}
      errorText={errorText || ''}
      showHelperArea={showHelperArea}
      validateValue={
        validateValue ||
        (val => !required || (val && val.value))
      }
      disabled={disabled}
      className={className}
      fill={fill}
    />
  )
}

ThemeSelectField.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  errorText: PropTypes.string,
  showHelperArea: PropTypes.bool,
  validateValue: PropTypes.func,
  user: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  fill: PropTypes.bool,
  showAllOption: PropTypes.bool,
  translate: PropTypes.func.isRequired,
  disableLoading: PropTypes.bool
}

const mapStateToProps = ({ auth }) => ({
  user: auth.user
})

const ThemeSelectFieldTranslated = translate(null, {
  wait: true,
  translateFuncName: 'translate'
})(ThemeSelectField)

export default connect(mapStateToProps)(ThemeSelectFieldTranslated)
export { ThemeSelectField }